import { t } from '@trimble/i18n';
import {
  JSX,
  Show,
  createEffect,
  createMemo,
  createSignal,
  on,
  onCleanup,
} from 'solid-js';
import { debounce } from '@solid-primitives/scheduled';
import {
  FileFolderItem,
  MissionDetailsSelection,
  RunDetailsSelection,
  isModelVisible,
  isPointCloudVisible,
  isTrajectoryVisible,
  useDataContext,
} from '@mobile-mapping/providers';
import { LoadState } from '@trimble/data-loading';
import {
  BtnClasses,
  CustomInput,
  Empty,
  IconButton,
  Spinner,
} from '@tcweb/connect-ui';
import PointCloudSelectionTreeContainer from './PointCloudSelectionTreeContainer';
import TrajectorySelectionTreeContainer from './TrajectorySelectionTreeContainer';
import ModelSelectionTreeContainer from './ModelSelectionTreeContainer';
import MissionSelectionTreeContainer from './MissionSelectionTreeContainer';
import { useSearchParams } from '@solidjs/router';
import {
  DropdownSelect,
  PanelLayoutWithHeaderToggle,
  SortableKeyOf,
  SortDirection,
  SortDropDownOption,
  SortDropDownSelect,
  sortItems,
} from '@trimble/ui-components/solidjs';
import { TreeNodeDefinition } from '@trimble/ui-components/solidjs';
import {
  PROJECT_DATA_FILTER_OPTIONS,
  ProjectDataFilterMode,
} from './project-data.types';
import {
  buildMissionNodes,
  buildModelNodes,
  buildPointCloudNodes,
  buildTrajectoryNodes,
  MODEL_ROOT_NODE_ID,
  POINT_CLOUD_ROOT_NODE_ID,
  TRAJECTORY_ROOT_NODE_ID,
  MissionTreeNodeType,
} from './project-data-tree-nodes';
import {
  countRootNodes,
  filterProjectDataNodes,
} from './project-data-filtering';
import { UnreachableCaseError } from 'ts-essentials';

type SortByKey = SortableKeyOf<Pick<TreeNodeDefinition, 'id' | 'label'>>;
type ChildrenLoadStatus = 'pending' | 'error' | 'success';
const DEBOUNCE_MS = 200;
const sortOptions: SortDropDownOption<SortByKey>[] = [
  {
    sortBy: 'label',
    text: t('mobileMapping.projectData.sortOptions.name'),
  },
];

const getChildrenLoadStatus = (state: LoadState): ChildrenLoadStatus =>
  state === LoadState.LoadComplete ? 'success' : 'pending';

export const ProjectDataSelectionTree = (props: {
  onModelDetailsSelect?(model: FileFolderItem): void;
  onRunDetailsSelect?(run: RunDetailsSelection): void;
  onMissionDetailsSelect?(mission: MissionDetailsSelection): void;
}): JSX.Element => {
  const { dataState } = useDataContext();

  const [searchParams, setSearchParams] = useSearchParams<{
    pointCloudIds?: string;
    trajectoryIds?: string;
    modelIds?: string;
    filterMode?: string;
  }>();

  const hasDataInUrl =
    !!searchParams.pointCloudIds ||
    !!searchParams.trajectoryIds ||
    !!searchParams.modelIds;

  const initialFilterMode: ProjectDataFilterMode =
    searchParams.filterMode === 'all' || searchParams.filterMode === 'active'
      ? searchParams.filterMode
      : hasDataInUrl
        ? 'active'
        : 'all';

  const [filterText, setFilterText] = createSignal<string>('');
  const [debouncedFilterText, setDebouncedFilterText] =
    createSignal<string>('');
  const [showSearch, setShowSearch] = createSignal<boolean>(false);
  const [sortBy, setSortBy] = createSignal<SortByKey>('label');
  const [sortDirection, setSortDirection] = createSignal<SortDirection>('asc');
  const [filterMode, setFilterMode] =
    createSignal<ProjectDataFilterMode>(initialFilterMode);

  const setFilterTextDebounced = debounce(setDebouncedFilterText, DEBOUNCE_MS);
  onCleanup(() => setFilterTextDebounced.clear());
  createEffect(() => setFilterTextDebounced(filterText()));

  createEffect(
    on(filterMode, (mode) => setSearchParams({ filterMode: mode }), {
      defer: true,
    })
  );

  const isAnyLoading = createMemo(() => {
    const isNotReady = (state: LoadState): boolean =>
      state === LoadState.Loading || state === LoadState.NotLoaded;

    return (
      isNotReady(dataState.trajectory.state) ||
      isNotReady(dataState.pointCloud.state) ||
      isNotReady(dataState.tcFileFolder.state) ||
      isNotReady(dataState.mission.state)
    );
  });

  const trajectoryNodes = createMemo(() =>
    dataState.trajectory.state === LoadState.LoadComplete
      ? buildTrajectoryNodes(Object.values(dataState.trajectory.items))
      : []
  );

  const pointCloudNodes = createMemo(() =>
    dataState.pointCloud.state === LoadState.LoadComplete
      ? buildPointCloudNodes(Object.values(dataState.pointCloud.items))
      : []
  );

  const modelNodes = createMemo(() =>
    dataState.tcFileFolder.state === LoadState.LoadComplete
      ? buildModelNodes(Object.values(dataState.tcFileFolder.items))
      : []
  );

  const missionNodes = createMemo(() =>
    dataState.mission.state === LoadState.LoadComplete
      ? buildMissionNodes(Object.values(dataState.mission.items))
      : []
  );

  const filteredTrajectoryNodes = createMemo(() =>
    filterProjectDataNodes(
      trajectoryNodes(),
      filterMode(),
      debouncedFilterText(),
      (node) =>
        node.id !== TRAJECTORY_ROOT_NODE_ID &&
        isTrajectoryVisible(dataState, node.id)
    )
  );

  const filteredPointCloudNodes = createMemo(() =>
    filterProjectDataNodes(
      pointCloudNodes(),
      filterMode(),
      debouncedFilterText(),
      (node) =>
        node.id !== POINT_CLOUD_ROOT_NODE_ID &&
        isPointCloudVisible(dataState, node.id)
    )
  );

  const filteredModelNodes = createMemo(() =>
    filterProjectDataNodes(
      modelNodes(),
      filterMode(),
      debouncedFilterText(),
      (node) =>
        node.id !== MODEL_ROOT_NODE_ID && isModelVisible(dataState, node.id)
    )
  );

  const filteredMissionNodes = createMemo(() =>
    filterProjectDataNodes(
      missionNodes(),
      filterMode(),
      debouncedFilterText(),
      (node) => {
        const type = node.data?.type;
        switch (type) {
          case MissionTreeNodeType.Run:
            return isPointCloudVisible(dataState, node.id);
          case MissionTreeNodeType.Path:
            return (
              isTrajectoryVisible(dataState, node.id) ||
              (node.data?.mission?.runs.some((run) =>
                isPointCloudVisible(dataState, run.id)
              ) ??
                false)
            );
          case MissionTreeNodeType.Mission:
          case MissionTreeNodeType.Runs:
          case undefined:
            return false;
          default:
            throw new UnreachableCaseError(type);
        }
      }
    )
  );

  const areAllFilteredTreesEmpty = createMemo(() => {
    return (
      countRootNodes(filteredTrajectoryNodes().nodes) === 0 &&
      countRootNodes(filteredPointCloudNodes().nodes) === 0 &&
      countRootNodes(filteredModelNodes().nodes) === 0 &&
      countRootNodes(filteredMissionNodes().nodes) === 0
    );
  });

  const filterKey = createMemo(
    () => `${filterMode()}:${debouncedFilterText()}`
  );

  const hasFiltersApplied = createMemo(() => {
    return filterMode() !== 'all' || debouncedFilterText().trim().length > 0;
  });

  const handleSortChanged = (
    sortBy: SortByKey,
    sortDirection: SortDirection
  ): void => {
    setSortBy(sortBy);
    setSortDirection(sortDirection);
  };

  function clearAndCloseSearch(): void {
    setFilterTextDebounced.clear();
    setFilterText('');
    setDebouncedFilterText('');
    setShowSearch(false);
  }

  const trajectoryControl = (
    <TrajectorySelectionTreeContainer
      nodes={filteredTrajectoryNodes().nodes}
      childrenLoadStatus={getChildrenLoadStatus(dataState.trajectory.state)}
      defaultExpandIds={filteredTrajectoryNodes().expandIds}
      filterKey={`${filterKey()}:${filteredTrajectoryNodes().expandIds.join(',')}`}
      highlightText={debouncedFilterText()}
      sortBy={sortBy()}
      sortDirection={sortDirection()}
      filterMode={filterMode()}
    />
  );

  const pointCloudControl = (
    <PointCloudSelectionTreeContainer
      nodes={filteredPointCloudNodes().nodes}
      childrenLoadStatus={getChildrenLoadStatus(dataState.pointCloud.state)}
      defaultExpandIds={filteredPointCloudNodes().expandIds}
      filterKey={`${filterKey()}:${filteredPointCloudNodes().expandIds.join(',')}`}
      highlightText={debouncedFilterText()}
      sortBy={sortBy()}
      sortDirection={sortDirection()}
      filterMode={filterMode()}
    />
  );

  const modelControl = (
    <ModelSelectionTreeContainer
      nodes={filteredModelNodes().nodes}
      childrenLoadStatus={getChildrenLoadStatus(dataState.tcFileFolder.state)}
      defaultExpandIds={filteredModelNodes().expandIds}
      filterKey={`${filterKey()}:${filteredModelNodes().expandIds.join(',')}`}
      highlightText={debouncedFilterText()}
      sortBy={sortBy()}
      sortDirection={sortDirection()}
      filterMode={filterMode()}
      onModelDetailsSelect={props.onModelDetailsSelect}
    />
  );

  const missionControl = (
    <MissionSelectionTreeContainer
      nodes={filteredMissionNodes().nodes}
      childrenLoadStatus={getChildrenLoadStatus(dataState.mission.state)}
      defaultExpandIds={
        hasFiltersApplied() ? filteredMissionNodes().expandIds : undefined
      }
      filterKey={`${filterKey()}:${filteredMissionNodes().expandIds.join(',')}`}
      highlightText={debouncedFilterText()}
      sortBy={sortBy()}
      sortDirection={sortDirection()}
      filterMode={filterMode()}
      onRunDetailsSelect={props.onRunDetailsSelect}
      onMissionDetailsSelect={props.onMissionDetailsSelect}
    />
  );

  // TODO: Merge these components into a single component that populates a single SelectionTree.
  // The entities trajectory, pointCloud, model can be aquired from respective providers
  // and merged into a single SelectionTree.
  interface ControlMap {
    control: JSX.Element;
    label: string;
  }

  const treeControls: ControlMap[] = [
    {
      control: trajectoryControl,
      label: t('mobileMapping.projectData.trajectories'),
    },
    {
      control: pointCloudControl,
      label: t('mobileMapping.projectData.pointClouds'),
    },
    {
      control: modelControl,
      label: t('mobileMapping.projectData.models'),
    },
    {
      control: missionControl,
      label: t('mobileMapping.missions.missions'),
    },
  ];

  const sortedTreeControls = createMemo(() => {
    const sortDir = sortDirection();
    return treeControls
      .sort((a, b) => sortItems(a, b, 'label', sortDir))
      .map(({ control }) => control);
  });

  return (
    <PanelLayoutWithHeaderToggle
      panelTitle={t('mobileMapping.projectData.projectData')}
      titleSectionRight={
        <div data-testid="filter-mode-dropdown">
          <DropdownSelect
            options={PROJECT_DATA_FILTER_OPTIONS()}
            value={filterMode()}
            onValueChange={(value) => value && setFilterMode(value)}
          />
        </div>
      }
      rightActions={
        <>
          <SortDropDownSelect
            options={sortOptions}
            sortBy={sortBy()}
            sortDirection={sortDirection()}
            onSortChanged={handleSortChanged}
          />
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="tc-icon-search"
            size="icon-medium"
            disabled={isAnyLoading()}
            clicked={() => setShowSearch(true)}
          />
        </>
      }
      toggleContent={
        <div class="flex items-center justify-between">
          <i class="icon-font tc-icon-search" />
          <CustomInput
            name="project-data-search"
            label=""
            class="w-full px-[0.5rem]"
            placeholder={t('general.search')}
            onInputRef={(ref) => {
              ref?.focus();
            }}
            onInput={(
              e: InputEvent & {
                currentTarget: HTMLInputElement;
                target: Element;
              }
            ) => setFilterText(e.currentTarget.value)}
          />
          <IconButton
            color={BtnClasses.TERTIARY}
            icon="tc-icon-content-close"
            size="icon-medium"
            clicked={clearAndCloseSearch}
          />
        </div>
      }
      showToggleContent={showSearch()}
      content={
        <>
          {isAnyLoading() ? (
            <div class="h-[300px]">
              <Spinner show={true} />
            </div>
          ) : (
            <Show
              when={!areAllFilteredTreesEmpty()}
              fallback={
                <Empty
                  icon="tc-icon-folder"
                  title={t('mobileMapping.projectData.noData.title')}
                  message={
                    hasFiltersApplied()
                      ? t('mobileMapping.projectData.noData.messageWithFilters')
                      : t('mobileMapping.projectData.noData.message')
                  }
                  data-testid="project-data-no-data-found"
                  isSmallContainer={true}
                />
              }
            >
              {sortedTreeControls()}
            </Show>
          )}
        </>
      }
    />
  );
};
